import {
  CleanType,
  Note,
  RealTimeTaskListSort,
  RealtimeRoomsListViewItem,
  RoomCleanTask,
  RoomCleanTaskStatus,
  Settings,
  Status,
  Task,
  TaskFilter,
  TaskVM,
} from '@app/core/async-services/http/versioned/task-management/task-management.interface';
import { addMinutes, isWithinDays } from '@app/core/utils/date/date.utils';
import { sum } from '@app/shared/utils/array.utils';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { Moment } from 'moment';

function isAssignedUnfinishedTask(task: TaskVM): boolean {
  return (
    !task.assigneeName.includes('unassigned') &&
    task.statusName !== RoomCleanTaskStatus.DONE &&
    task.statusName !== RoomCleanTaskStatus.INSPECTED
  );
}

function isMyRecentlyFinishedRoomCleanTask(task: TaskVM): boolean {
  return (
    task.isAssignedToCurrentUser &&
    isWithinDays(task.createdOn, 14) &&
    (task.statusName === RoomCleanTaskStatus.DONE ||
      task.statusName === RoomCleanTaskStatus.INSPECTED ||
      task.statusName === RoomCleanTaskStatus.DECLINED_SERVICE) &&
    task.typeName === 'Room Clean'
  );
}

function isMyUnfinishedRoomCleanTask(task: TaskVM): boolean {
  return (
    task.isAssignedToCurrentUser &&
    (task.statusName === RoomCleanTaskStatus.DIRTY ||
      task.statusName === RoomCleanTaskStatus.CLEANING ||
      task.statusName === RoomCleanTaskStatus.DND ||
      task.statusName === RoomCleanTaskStatus.RETURN_LATER) &&
    task.typeName === 'Room Clean'
  );
}

function isOpenTask(task: TaskVM): boolean {
  return task.statusName !== Status.DONE;
}

function isRecentlyFinishedTask(task: TaskVM): boolean {
  return (
    isWithinDays(task.modifiedOn, 7) &&
    (task.statusName === Status.DONE ||
      task.statusName === RoomCleanTaskStatus.INSPECTED)
  );
}

function isUnassignedOpenTask(task: TaskVM): boolean {
  return (
    task.assigneeName.includes('unassigned') && task.statusName === Status.OPEN
  );
}

function isUnfinishedGuestRequestTask(task: TaskVM): boolean {
  return task.statusName !== Status.DONE && task.guestRequested;
}

export function getTaskFilterFunction(
  filter: TaskFilter,
): (taskVM: TaskVM) => boolean {
  if (filter === TaskFilter.ASSIGNED_UNFINISHED_TASKS) {
    return isAssignedUnfinishedTask;
  } else if (filter === TaskFilter.MY_RECENTLY_FINISHED_ROOM_CLEAN_TASKS) {
    return isMyRecentlyFinishedRoomCleanTask;
  } else if (filter === TaskFilter.MY_UNFINISHED_ROOM_CLEAN_TASKS) {
    return isMyUnfinishedRoomCleanTask;
  } else if (filter === TaskFilter.OPEN_TASKS) {
    return isOpenTask;
  } else if (filter === TaskFilter.RECENTLY_FINISHED_TASKS) {
    return isRecentlyFinishedTask;
  } else if (filter === TaskFilter.UNASSIGNED_OPEN_TASKS) {
    return isUnassignedOpenTask;
  } else if (filter === TaskFilter.UNFINISHED_GUEST_REQUESTS) {
    return isUnfinishedGuestRequestTask;
  } else {
    return () => false;
  }
}

export function isRoomCleanTask(task: Task): task is RoomCleanTask {
  return task.typeName === 'Room Clean';
}

export function getFullCleanEndTimeRealtimeRooms({
  assigneeDictionary,
  settings,
  taskDictionary,
  cleanTypeDictionary,
  currentTime,
}: {
  assigneeDictionary: { [assigneeUniqueId: string]: RoomCleanTask[] };
  settings: Settings | undefined;
  taskDictionary: { [taskId: string]: Task };
  cleanTypeDictionary: { [cleanTypeId: string]: CleanType };
  currentTime: string;
}): string[] {
  const assigneeKeys = Object.keys(assigneeDictionary);
  const fullCleanEndTimes = assigneeKeys
    .flatMap((key) => {
      const formattedCurrentTime = moment(currentTime).format('HH:mm:ss');
      const firstRoomStartTime = addMinutes(
        formattedCurrentTime,
        settings?.postShiftWorkMinutes ?? 0,
      );
      const cache = { previousRoomEndTime: firstRoomStartTime };
      return assigneeDictionary[key]
        .filter((task) => task.statusName !== Status.DONE)
        .map(
          (task) =>
            cleanTypeDictionary[
              (taskDictionary[task.id] as RoomCleanTask).details.cleanTypeId
            ],
        )
        .map(addEndTime(cache));
    })
    .filter(isFullClean)
    .map((clean) => clean.endTime)
    .sort();
  return fullCleanEndTimes;
}

export function getRealtimeRoomsLatestEndOfCleaning(
  assigneeDictionary: { [assigneeUniqueId: string]: RoomCleanTask[] },
  settings: Settings | undefined,
  currentTime: string,
  cleanTypeDictionary: { [cleanTypeId: string]: CleanType },
): Moment | 'No Room Cleans Published' {
  const assigneeValues = Object.values(assigneeDictionary);
  let minutesLeftForNotDoneCleans: number;
  let latestTime: Moment = moment(currentTime);
  if (assigneeValues.length === 0) {
    return 'No Room Cleans Published';
  }
  assigneeValues.forEach((value, index) => {
    minutesLeftForNotDoneCleans = value
      .filter((task) => task.statusName !== Status.DONE)
      .map((task) => task.details.cleanTypeId)
      .map((cleanTypeId) => cleanTypeDictionary[cleanTypeId].mpr)
      .reduce(sum, 0);
    const endOfCleaning = moment(currentTime).add(
      minutesLeftForNotDoneCleans + (settings?.postShiftWorkMinutes ?? 0),
      'm',
    );
    if (index === 0) {
      latestTime = endOfCleaning;
    } else if (endOfCleaning.isAfter(latestTime)) {
      latestTime = endOfCleaning;
    }
  });
  return latestTime;
}

export function getArrivalReadyTime(
  assignedFullCleans: number,
  fullCleanEndTimes: string[],
  minimumFullCleansForArrivals: number,
  todayEstimatedArrivals: number,
): {
  arrivalReadyTime: string;
  isReadyTimeTooLate: boolean;
} {
  if (todayEstimatedArrivals <= 0) {
    return {
      arrivalReadyTime: 'No Arrivals',
      isReadyTimeTooLate: false,
    };
  } else if (minimumFullCleansForArrivals > assignedFullCleans) {
    return {
      arrivalReadyTime: 'Not Enough Assigned',
      isReadyTimeTooLate: false,
    };
  } else if (
    minimumFullCleansForArrivals > 0 &&
    fullCleanEndTimes.length >= minimumFullCleansForArrivals
  ) {
    const time = fullCleanEndTimes[minimumFullCleansForArrivals - 1];
    return {
      arrivalReadyTime: moment(time, 'HH:mm:ss').format('h:mm A'),
      isReadyTimeTooLate: moment(time, 'HH:mm:ss').isSameOrAfter(
        moment('15:00:00', 'HH:mm:ss'),
        'hour',
      ),
    };
  } else {
    return {
      arrivalReadyTime: 'Ready!',
      isReadyTimeTooLate: false,
    };
  }
}

export function sortTaskNote(a: Note, b: Note) {
  if (a.createdOn < b.createdOn) {
    return -1;
  } else if (a.createdOn > b.createdOn) {
    return 1;
  } else {
    return 0;
  }
}

export function getTaskNotesVM(notes: Note[]): Note[] {
  const clonedNotes = cloneDeep(notes);
  const uniqueNotes: Note[] = [];
  clonedNotes.forEach((note) => {
    if (uniqueNotes.every((uniqueNote) => uniqueNote.id !== note.id)) {
      uniqueNotes.push(note);
    }
  });

  return uniqueNotes.sort(sortTaskNote).map(formatTaskNote);
}

function addEndTime(cache: { previousRoomEndTime: string }) {
  return (cleanType: CleanType): CleanType & { endTime: string } => {
    const endTime = addMinutes(cache.previousRoomEndTime, cleanType.mpr);
    cache.previousRoomEndTime = endTime;
    return { ...cleanType, endTime };
  };
}

function isFullClean(cleanType: CleanType): boolean {
  return cleanType.metaType === 'full';
}

export function formatTaskNote(note: Note): Note {
  return {
    ...note,
    createdOn: moment(note.createdOn).format('MM/DD/YYYY LT'),
  };
}

export function roomAttendantViewSort(
  a: RealtimeRoomsListViewItem,
  b: RealtimeRoomsListViewItem,
) {
  return a.assignee > b.assignee
    ? 1
    : a.assignee === b.assignee
    ? a.sortOrder > b.sortOrder
      ? 1
      : -1
    : -1;
}

export function getRealtimeListViewSort(sort: RealTimeTaskListSort) {
  const column = sort.column;
  if (column !== null) {
    if (column === 'hasUnreadNote') {
      return (a: RealtimeRoomsListViewItem, b: RealtimeRoomsListViewItem) => {
        //always keep tasks with notes on top, sort read vs unread
        if (a.notes.length > 0 && b.notes.length === 0) {
          return -1;
        } else if (b.notes.length > 0 && a.notes.length === 0) {
          return 1;
        } else if (a[column] > b[column]) {
          return sort.orderBy === 'asc' ? -1 : 1;
        } else if (a[column] < b[column]) {
          return sort.orderBy === 'asc' ? 1 : -1;
        } else {
          return sortByRoomNumber(a, b);
        }
      };
    }
    if (column === 'changeLinen') {
      return (a: RealtimeRoomsListViewItem, b: RealtimeRoomsListViewItem) => {
        if (a[column] > b[column]) {
          return sort.orderBy === 'asc' ? 1 : -1;
        } else if (a[column] < b[column]) {
          return sort.orderBy === 'asc' ? -1 : 1;
        } else {
          return sortByRoomNumber(a, b);
        }
      };
    } else {
      return (a: RealtimeRoomsListViewItem, b: RealtimeRoomsListViewItem) => {
        if (a[column].toLowerCase() > b[column].toLowerCase()) {
          return sort.orderBy === 'asc' ? 1 : -1;
        } else if (a[column].toLowerCase() < b[column].toLowerCase()) {
          return sort.orderBy === 'asc' ? -1 : 1;
        } else if (column !== 'roomNumber') {
          return sortByRoomNumber(a, b);
        } else {
          return 0;
        }
      };
    }
  }
}

export function sortByRoomNumber(
  a: RealtimeRoomsListViewItem,
  b: RealtimeRoomsListViewItem,
) {
  if (a.roomNumber.toLowerCase() > b.roomNumber.toLowerCase()) {
    return 1;
  } else if (a.roomNumber.toLowerCase() < b.roomNumber.toLowerCase()) {
    return -1;
  } else {
    return 0;
  }
}

export function hasUnreadNote(notes: Note[]): boolean {
  return notes?.length
    ? notes
        .filter((note) => !note.isMyNote)
        .map((note) => note.taskNoteRead)
        .some((taskNoteReadAt) => !taskNoteReadAt)
    : false;
}
